// src/components/admin/SystemMaintenance.tsx - COMPLETE FILE
import React, { useState, useEffect } from 'react';
import { 
  Users, CreditCard, Terminal, Activity, BarChart as ChartBar, 
  FileText, Bell, Settings, Building2, Zap, RefreshCw, 
  AlertTriangle, CheckCircle, Trash2, Target, TrendingUp,
  Database, Gauge, Search, Filter, Calendar
} from 'lucide-react';
import { SamApiTest } from '../../lib/sam-api/test';
import { EnhancedSamApiTest } from '../../lib/sam-api/enhanced-test';
import { SamResponseDebugger } from '../../lib/sam-api/response-debugger';
import { DebugRawSamData } from '../../lib/sam-api/debug-raw-data';
import { verifyDataPipeline } from '../../lib/sam-api/verify';
import { supabase } from '../../lib/supabase';
import { getProfileData } from '../../lib/get-profile';
import toast from 'react-hot-toast';

interface MaintenanceTask {
  id: string;
  name: string;
  description: string;
  status: 'idle' | 'running' | 'success' | 'error';
  lastRun?: string;
  error?: string;
  category?: 'sam-integration' | 'database' | 'system' | 'analysis';
  results?: any;
}

interface DataQualityStats {
  totalContracts: number;
  qualityBreakdown: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
  };
  extractionRates: {
    richDescriptions: number;
    fundingInfo: number;
    documents: number;
    contactInfo: number;
  };
}

interface CustomSearchParams {
  naicsCode: string;
  agency: string;
  noticeId: string;
  keyword: string;
  postedFrom: string;
  postedTo: string;
  size: number;
}

export function SystemMaintenance() {
  const [tasks, setTasks] = useState<MaintenanceTask[]>([
    // SAM.gov Integration Tests
    {
      id: 'enhanced-sam-test',
      name: 'Enhanced SAM.gov API Test',
      description: 'Run comprehensive SAM.gov integration test with quality analysis and rich data extraction',
      status: 'idle',
      category: 'sam-integration'
    },
    {
      id: 'custom-sam-search',
      name: 'Custom SAM.gov Search Test',
      description: 'Test SAM.gov API with custom search parameters (NAICS, Agency, Notice ID, etc.)',
      status: 'idle',
      category: 'sam-integration'
    },
    {
      id: 'sam-api-test',
      name: 'Basic SAM.gov API Test',
      description: 'Run basic integration test with sample contract data',
      status: 'idle',
      category: 'sam-integration'
    },
    {
      id: 'pipeline-verify',
      name: 'Data Pipeline Verification',
      description: 'Verify data pipeline integrity and field mapping',
      status: 'idle',
      category: 'sam-integration'
    },
    
    // Data Quality Analysis
    {
      id: 'quality-analysis',
      name: 'Data Quality Analysis',
      description: 'Analyze extraction quality and generate improvement recommendations',
      status: 'idle',
      category: 'analysis'
    },
    {
      id: 'debug-raw-data',
      name: 'Debug Raw SAM.gov Data',
      description: 'Inspect the raw data structure from SAM.gov to improve extraction',
      status: 'idle',
      category: 'analysis'
    },
    {
      id: 'contract-quality-audit',
      name: 'Contract Quality Audit',
      description: 'Audit contract data completeness and scoring accuracy',
      status: 'idle',
      category: 'analysis'
    },
    
    // Database & System Tasks
    {
      id: 'cleanup-duplicates',
      name: 'Cleanup Duplicate Contracts (coming soon)',
      description: 'Not yet implemented — remove duplicate contract entries and optimize database',
      status: 'idle',
      category: 'database'
    },
    {
      id: 'reindex-search',
      name: 'Reindex Search Database (coming soon)',
      description: 'Not yet implemented — rebuild search indexes for improved query performance',
      status: 'idle',
      category: 'database'
    },
    {
      id: 'cache-warm',
      name: 'Warm Application Cache (coming soon)',
      description: 'Not yet implemented — pre-load frequently accessed data into cache',
      status: 'idle',
      category: 'system'
    }
  ]);

  const [logs, setLogs] = useState<string[]>([]);
  const [qualityStats, setQualityStats] = useState<DataQualityStats | null>(null);
  const [showCustomSearch, setShowCustomSearch] = useState(false);
  const [customSearchParams, setCustomSearchParams] = useState<CustomSearchParams>({
    naicsCode: '',
    agency: '',
    noticeId: '',
    keyword: '',
    postedFrom: '',
    postedTo: '',
    size: 10
  });

  useEffect(() => {
    loadQualityStats();
  }, []);

  const loadQualityStats = async () => {
    try {
      const { data, error } = await supabase
        .from('sam_contracts')
        .select('*')
        .limit(100);

      if (error) throw error;

      if (data && data.length > 0) {
        // Calculate quality stats from existing data
        const stats = calculateQualityStats(data);
        setQualityStats(stats);
      }
    } catch (error) {
      console.error('Failed to load quality stats:', error);
    }
  };

  const calculateQualityStats = (contracts: any[]): DataQualityStats => {
    const stats = {
      totalContracts: contracts.length,
      qualityBreakdown: { excellent: 0, good: 0, fair: 0, poor: 0 },
      extractionRates: {
        richDescriptions: 0,
        fundingInfo: 0,
        documents: 0,
        contactInfo: 0
      }
    };

    contracts.forEach(contract => {
      let score = 0;
      
      // Rich description
      if (contract.description && contract.description.length > 200) {
        stats.extractionRates.richDescriptions++;
        score += 2;
      }
      
      // Funding info
      if (contract.funding_amount || contract.award_amount) {
        stats.extractionRates.fundingInfo++;
        score += 2;
      }
      
      // Contact info
      if (contract.point_of_contact) {
        stats.extractionRates.contactInfo++;
        score += 2;
      }
      
      // Documents (placeholder - would need to check related table)
      // stats.extractionRates.documents++;
      
      // Quality scoring
      if (score >= 6) stats.qualityBreakdown.excellent++;
      else if (score >= 4) stats.qualityBreakdown.good++;
      else if (score >= 2) stats.qualityBreakdown.fair++;
      else stats.qualityBreakdown.poor++;
    });

    // Convert to percentages
    stats.extractionRates.richDescriptions = (stats.extractionRates.richDescriptions / contracts.length) * 100;
    stats.extractionRates.fundingInfo = (stats.extractionRates.fundingInfo / contracts.length) * 100;
    stats.extractionRates.contactInfo = (stats.extractionRates.contactInfo / contracts.length) * 100;
    stats.extractionRates.documents = (stats.extractionRates.documents / contracts.length) * 100;

    return stats;
  };

  const updateTaskStatus = (taskId: string, updates: Partial<MaintenanceTask>) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId ? { ...task, ...updates } : task
    ));
  };

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toISOString()}] ${message}`]);
  };

  const runEnhancedSamTest = async () => {
    const taskId = 'enhanced-sam-test';
    try {
      updateTaskStatus(taskId, { status: 'running' });
      addLog('🚀 Starting Enhanced SAM.gov API integration test...');

      const results = await EnhancedSamApiTest.runEnhancedTest();
      
      // Detailed logging based on results
      if (results.processed === 0) {
        addLog('❌ No contracts were processed - API connectivity issue detected');
        
        // Show specific error details from the diagnostic
        if (results.errors && results.errors.length > 0) {
          addLog('🔍 Specific Error Details:');
          results.errors.forEach(err => {
            addLog(`   - ${err.noticeId}: ${err.error}`);
            
            // Provide specific troubleshooting based on error type
            if (err.noticeId === 'SUPABASE') {
              addLog('   💡 SUPABASE ISSUE: Check database connection and credentials');
            } else if (err.noticeId === 'EDGE_FUNCTION') {
              addLog('   💡 EDGE FUNCTION ISSUE: Deploy sam-api-proxy function to Supabase');
              addLog('   📝 Run: supabase functions deploy sam-api-proxy');
            } else if (err.noticeId === 'SAM_API_KEY') {
              addLog('   💡 API KEY ISSUE: Set SAM_API_KEY environment variable in Supabase');
              addLog('   📝 Go to Supabase Dashboard → Settings → Environment Variables');
            } else if (err.noticeId === 'CONTRACT_FETCH') {
              addLog('   💡 DATA FETCH ISSUE: SAM.gov API may be down or slow');
            }
          });
        } else {
          addLog('🔍 Check browser console for additional error information');
          addLog('💡 Possible issues:');
          addLog('   - SAM.gov API key not configured');
          addLog('   - Supabase Edge Function not deployed');
          addLog('   - Network connectivity problems');
          addLog('   - SAM.gov API temporarily unavailable');
        }
        
        updateTaskStatus(taskId, {
          status: 'error',
          error: 'No contracts processed - API connectivity issue',
          lastRun: new Date().toISOString(),
          results: results
        });
        
        toast.error('Enhanced SAM.gov API test failed - Check logs for details');
        return;
      }

      addLog(`✅ Test completed successfully!`);
      addLog(`📊 Processed: ${results.processed} contracts`);
      addLog(`🎯 Success Rate: ${((results.successful / results.processed) * 100).toFixed(1)}%`);
      addLog(`⭐ Quality Breakdown:`);
      addLog(`   - Excellent: ${results.qualityBreakdown.excellent}`);
      addLog(`   - Good: ${results.qualityBreakdown.good}`);
      addLog(`   - Fair: ${results.qualityBreakdown.fair}`);
      addLog(`   - Poor: ${results.qualityBreakdown.poor}`);

      addLog(`🎯 Data Extraction Success Rates:`);
      addLog(`   - Rich Descriptions: ${((results.dataExtractionStats.hasRichDescription / results.processed) * 100).toFixed(1)}%`);
      addLog(`   - Funding Information: ${((results.dataExtractionStats.hasFundingInfo / results.processed) * 100).toFixed(1)}%`);
      addLog(`   - Documents: ${((results.dataExtractionStats.hasDocuments / results.processed) * 100).toFixed(1)}%`);
      addLog(`   - Contact Info: ${((results.dataExtractionStats.hasContactInfo / results.processed) * 100).toFixed(1)}%`);

      // Update quality stats for display
      setQualityStats({
        totalContracts: results.processed,
        qualityBreakdown: results.qualityBreakdown,
        extractionRates: {
          richDescriptions: (results.dataExtractionStats.hasRichDescription / results.processed) * 100,
          fundingInfo: (results.dataExtractionStats.hasFundingInfo / results.processed) * 100,
          documents: (results.dataExtractionStats.hasDocuments / results.processed) * 100,
          contactInfo: (results.dataExtractionStats.hasContactInfo / results.processed) * 100,
        }
      });

      if (results.contractTypes) {
        addLog(`📋 Contract Types Found:`);
        Object.entries(results.contractTypes).forEach(([type, count]) => {
          addLog(`   - ${type}: ${count}`);
        });
      }

      if (results.errors.length > 0) {
        addLog(`⚠️ Errors encountered:`);
        results.errors.forEach(err => {
          addLog(`   - ${err.noticeId}: ${err.error}`);
        });
      }

      updateTaskStatus(taskId, {
        status: 'success',
        lastRun: new Date().toISOString(),
        results: results
      });
      
      // Reload quality stats after successful test
      loadQualityStats();
      
      toast.success('Enhanced SAM.gov API test completed successfully');
    } catch (error) {
      console.error('Error running enhanced SAM.gov API test:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      updateTaskStatus(taskId, {
        status: 'error',
        error: errorMessage
      });
      addLog(`❌ Error: ${errorMessage}`);
      toast.error('Enhanced SAM.gov API test failed');
    }
  };

  const runCustomSamSearch = async () => {
    const taskId = 'custom-sam-search';
    try {
      updateTaskStatus(taskId, { status: 'running' });
      addLog('🔍 Starting Custom SAM.gov Search Test...');
      
      // Build search parameters
      const searchOptions: any = {
        size: customSearchParams.size,
        debug: true
      };

      if (customSearchParams.naicsCode) {
        searchOptions.naicsCode = customSearchParams.naicsCode;
        addLog(`🎯 Filtering by NAICS Code: ${customSearchParams.naicsCode}`);
      }
      
      if (customSearchParams.agency) {
        searchOptions.agency = customSearchParams.agency;
        addLog(`🏛️ Filtering by Agency: ${customSearchParams.agency}`);
      }
      
      if (customSearchParams.keyword) {
        searchOptions.keyword = customSearchParams.keyword;
        addLog(`🔍 Filtering by Keyword: ${customSearchParams.keyword}`);
      }
      
      if (customSearchParams.postedFrom && customSearchParams.postedTo) {
        // Convert from YYYY-MM-DD to MM/DD/YYYY format for SAM.gov
        const formatDateForSAM = (dateStr: string) => {
          const date = new Date(dateStr);
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const year = date.getFullYear();
          return `${month}/${day}/${year}`;
        };
        
        searchOptions.postedFrom = formatDateForSAM(customSearchParams.postedFrom);
        searchOptions.postedTo = formatDateForSAM(customSearchParams.postedTo);
        addLog(`📅 Date Range: ${searchOptions.postedFrom} to ${searchOptions.postedTo}`);
      }

      // Import samApi and run search
      const { samApi } = await import('../../lib/sam-api/client');
      const results = await samApi.searchContracts(searchOptions);
      
      addLog(`✅ Custom search completed!`);
      addLog(`📊 Found: ${results.items.length} contracts`);
      addLog(`📈 Total Available: ${results.totalRecords}`);
      addLog(`📄 Page: ${results.page + 1}, Size: ${results.size}`);
      
      if (results.items.length > 0) {
        addLog(`📋 Sample Results:`);
        results.items.slice(0, 3).forEach((contract, index) => {
          addLog(`   ${index + 1}. ${contract.title} (${contract.noticeId})`);
          addLog(`      Agency: ${typeof contract.agency === 'object' ? contract.agency?.name : contract.agency}`);
          addLog(`      NAICS: ${contract.naicsCode || 'N/A'}`);
        });
      } else {
        addLog('⚠️ No contracts found with these search criteria');
        addLog('💡 Try adjusting your search parameters');
      }

      updateTaskStatus(taskId, {
        status: 'success',
        lastRun: new Date().toISOString(),
        results: {
          found: results.items.length,
          totalRecords: results.totalRecords,
          searchParams: searchOptions
        }
      });
      
      toast.success(`Custom search found ${results.items.length} contracts`);
    } catch (error) {
      console.error('Error running custom SAM search:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      updateTaskStatus(taskId, {
        status: 'error',
        error: errorMessage
      });
      addLog(`❌ Error: ${errorMessage}`);
      toast.error('Custom SAM search failed');
    }
  };

  const runSamApiTest = async () => {
    const taskId = 'sam-api-test';
    try {
      updateTaskStatus(taskId, { status: 'running' });
      addLog('Starting basic SAM.gov API integration test...');

      const results = await SamApiTest.runTest();
      
      addLog(`Processed ${results.processed} contracts`);
      addLog(`Successfully imported: ${results.successful}`);
      addLog(`Failed imports: ${results.failed}`);

      if (results.errors.length > 0) {
        results.errors.forEach(err => {
          addLog(`Error with ${err.noticeId}: ${err.error}`);
        });
      }

      updateTaskStatus(taskId, {
        status: 'success',
        lastRun: new Date().toISOString()
      });
      
      toast.success('SAM.gov API test completed successfully');
    } catch (error) {
      console.error('Error running SAM.gov API test:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      updateTaskStatus(taskId, {
        status: 'error',
        error: errorMessage
      });
      addLog(`Error: ${errorMessage}`);
      toast.error('SAM.gov API test failed');
    }
  };

  const runDebugRawData = async () => {
    const taskId = 'debug-raw-data';
    try {
      updateTaskStatus(taskId, { status: 'running' });
      addLog('🔍 Starting raw SAM.gov data inspection...');

      // Import and run the debug function
      await DebugRawSamData.inspectRawData();
      
      addLog('✅ Raw data inspection complete - check browser console for detailed output');
      addLog('💡 Look for the raw SAM.gov data structure to understand available fields');

      updateTaskStatus(taskId, {
        status: 'success',
        lastRun: new Date().toISOString()
      });
      
      toast.success('Raw data inspection completed - check console');
    } catch (error) {
      console.error('Error inspecting raw data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      updateTaskStatus(taskId, {
        status: 'error',
        error: errorMessage
      });
      addLog(`❌ Error: ${errorMessage}`);
      toast.error('Raw data inspection failed');
    }
  };

  const runPipelineVerify = async () => {
    const taskId = 'pipeline-verify';
    try {
      updateTaskStatus(taskId, { status: 'running' });
      addLog('Verifying data pipeline integrity...');

      const results = await verifyDataPipeline();
      
      addLog(`Pipeline verification completed: ${results.success ? 'SUCCESS' : 'FAILED'}`);
      addLog(`Sample contracts found: ${results.contractCount}`);

      updateTaskStatus(taskId, {
        status: results.success ? 'success' : 'error',
        lastRun: new Date().toISOString()
      });
      
      toast.success('Data pipeline verification completed');
    } catch (error) {
      console.error('Error verifying pipeline:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      updateTaskStatus(taskId, {
        status: 'error',
        error: errorMessage
      });
      addLog(`Error: ${errorMessage}`);
      toast.error('Pipeline verification failed');
    }
  };

  const renderTaskButton = (task: MaintenanceTask) => {
    const isRunning = task.status === 'running';
    const isError = task.status === 'error';
    const isSuccess = task.status === 'success';

    let icon = <Target className="h-4 w-4" />;
    let buttonClass = 'bg-bw-primary hover:bg-bw-primary';

    if (isRunning) {
      icon = <RefreshCw className="h-4 w-4 animate-spin" />;
      buttonClass = 'bg-fit-moderate-bg0 cursor-not-allowed';
    } else if (isError) {
      icon = <AlertTriangle className="h-4 w-4" />;
      buttonClass = 'bg-urgent-bg0 hover:bg-urgent';
    } else if (isSuccess) {
      icon = <CheckCircle className="h-4 w-4" />;
      buttonClass = 'bg-success hover:bg-success';
    }

    const handleClick = () => {
      switch (task.id) {
        case 'enhanced-sam-test':
          runEnhancedSamTest();
          break;
        case 'custom-sam-search':
          runCustomSamSearch();
          break;
        case 'sam-api-test':
          runSamApiTest();
          break;
        case 'debug-raw-data':
          runDebugRawData();
          break;
        case 'pipeline-verify':
          runPipelineVerify();
          break;
        default:
          toast(`${task.name} - Coming soon`, {
            icon: 'ℹ️',
            duration: 3000
          });
      }
    };

    return (
      <button
        onClick={handleClick}
        disabled={isRunning}
        className={`${buttonClass} text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm font-medium transition-colors duration-200`}
      >
        {icon}
        <span>{isRunning ? 'Running...' : 'Run'}</span>
      </button>
    );
  };

  const renderCustomSearchForm = () => {
    if (!showCustomSearch) return null;

    return (
      <div className="mt-4 p-4 bg-bw-surface-subtle rounded-lg border">
        <h4 className="font-medium text-bw-ink mb-3">Custom Search Parameters</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">
              NAICS Code
            </label>
            <input
              type="text"
              value={customSearchParams.naicsCode}
              onChange={(e) => setCustomSearchParams(prev => ({ ...prev, naicsCode: e.target.value }))}
              placeholder="e.g., 541511"
              className="w-full px-3 py-2 border border-bw-border rounded-md focus:outline-none focus:ring-2 focus:ring-bw-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">
              Agency
            </label>
            <input
              type="text"
              value={customSearchParams.agency}
              onChange={(e) => setCustomSearchParams(prev => ({ ...prev, agency: e.target.value }))}
              placeholder="e.g., Department of Defense"
              className="w-full px-3 py-2 border border-bw-border rounded-md focus:outline-none focus:ring-2 focus:ring-bw-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">
              Notice ID
            </label>
            <input
              type="text"
              value={customSearchParams.noticeId}
              onChange={(e) => setCustomSearchParams(prev => ({ ...prev, noticeId: e.target.value }))}
              placeholder="e.g., 70Z03824Q00000001"
              className="w-full px-3 py-2 border border-bw-border rounded-md focus:outline-none focus:ring-2 focus:ring-bw-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">
              Keyword
            </label>
            <input
              type="text"
              value={customSearchParams.keyword}
              onChange={(e) => setCustomSearchParams(prev => ({ ...prev, keyword: e.target.value }))}
              placeholder="e.g., software development"
              className="w-full px-3 py-2 border border-bw-border rounded-md focus:outline-none focus:ring-2 focus:ring-bw-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">
              Posted From
            </label>
            <input
              type="date"
              value={customSearchParams.postedFrom}
              onChange={(e) => setCustomSearchParams(prev => ({ ...prev, postedFrom: e.target.value }))}
              className="w-full px-3 py-2 border border-bw-border rounded-md focus:outline-none focus:ring-2 focus:ring-bw-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">
              Posted To
            </label>
            <input
              type="date"
              value={customSearchParams.postedTo}
              onChange={(e) => setCustomSearchParams(prev => ({ ...prev, postedTo: e.target.value }))}
              className="w-full px-3 py-2 border border-bw-border rounded-md focus:outline-none focus:ring-2 focus:ring-bw-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">
              Results Size
            </label>
            <select
              value={customSearchParams.size}
              onChange={(e) => setCustomSearchParams(prev => ({ ...prev, size: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-bw-border rounded-md focus:outline-none focus:ring-2 focus:ring-bw-primary"
            >
              <option value={5}>5 contracts</option>
              <option value={10}>10 contracts</option>
              <option value={25}>25 contracts</option>
              <option value={50}>50 contracts</option>
              <option value={100}>100 contracts</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex space-x-2">
          <button
            onClick={() => setShowCustomSearch(false)}
            className="px-4 py-2 text-bw-muted bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setShowCustomSearch(false);
              // Will be triggered by the main button
            }}
            className="px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-bw-primary"
          >
            Apply Parameters
          </button>
        </div>
      </div>
    );
  };

  const tasksByCategory = tasks.reduce((acc, task) => {
    const category = task.category || 'system';
    if (!acc[category]) acc[category] = [];
    acc[category].push(task);
    return acc;
  }, {} as Record<string, MaintenanceTask[]>);

  const categoryNames = {
    'sam-integration': 'SAM.gov Integration',
    'analysis': 'Data Analysis',
    'database': 'Database Maintenance',
    'system': 'System Maintenance'
  };

  const categoryIcons = {
    'sam-integration': Building2,
    'analysis': ChartBar,
    'database': Database,
    'system': Settings
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-bw-ink">System Maintenance</h1>
          <p className="text-bw-muted">Monitor and maintain system components</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setLogs([])}
            className="flex items-center space-x-2 px-4 py-2 bg-neutral-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            <span>Clear Logs</span>
          </button>
        </div>
      </div>

      {/* Quality Stats Overview */}
      {qualityStats && (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-bw-surface p-4 rounded-lg border shadow-sm">
            <div className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-bw-primary" />
              <span className="font-medium text-bw-ink">Total Contracts</span>
            </div>
            <p className="text-2xl font-bold text-bw-primary mt-1">{qualityStats.totalContracts}</p>
          </div>
          <div className="bg-bw-surface p-4 rounded-lg border shadow-sm">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-success" />
              <span className="font-medium text-bw-ink">Rich Descriptions</span>
            </div>
            <p className="text-2xl font-bold text-success mt-1">{qualityStats.extractionRates.richDescriptions.toFixed(1)}%</p>
          </div>
          <div className="bg-bw-surface p-4 rounded-lg border shadow-sm">
            <div className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5 text-bw-primary" />
              <span className="font-medium text-bw-ink">Funding Info</span>
            </div>
            <p className="text-2xl font-bold text-bw-primary mt-1">{qualityStats.extractionRates.fundingInfo.toFixed(1)}%</p>
          </div>
          <div className="bg-bw-surface p-4 rounded-lg border shadow-sm">
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-fit-moderate" />
              <span className="font-medium text-bw-ink">Contact Info</span>
            </div>
            <p className="text-2xl font-bold text-fit-moderate mt-1">{qualityStats.extractionRates.contactInfo.toFixed(1)}%</p>
          </div>
        </div>
      )}

      {/* Task Categories */}
      <div className="space-y-6">
        {Object.entries(tasksByCategory).map(([category, categoryTasks]) => {
          const CategoryIcon = categoryIcons[category as keyof typeof categoryIcons];
          return (
            <div key={category} className="bg-bw-surface rounded-lg border shadow-sm">
              <div className="px-6 py-4 border-b border-bw-border">
                <div className="flex items-center space-x-3">
                  <CategoryIcon className="h-5 w-5 text-bw-muted" />
                  <h2 className="text-lg font-semibold text-bw-ink">
                    {categoryNames[category as keyof typeof categoryNames]}
                  </h2>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {categoryTasks.map((task) => (
                    <div key={task.id} className="flex items-center justify-between p-4 bg-bw-surface-subtle rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-medium text-bw-ink">{task.name}</h3>
                          {task.status === 'success' && task.lastRun && (
                            <span className="text-xs text-bw-muted">
                              Last run: {new Date(task.lastRun).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-bw-muted">{task.description}</p>
                        {task.error && (
                          <p className="text-sm text-urgent mt-1">Error: {task.error}</p>
                        )}
                        {task.id === 'custom-sam-search' && (
                          <div className="mt-2">
                            <button
                              onClick={() => setShowCustomSearch(!showCustomSearch)}
                              className="text-sm text-bw-primary hover:text-bw-primary flex items-center space-x-1"
                            >
                              <Filter className="h-4 w-4" />
                              <span>Configure Search Parameters</span>
                            </button>
                            {renderCustomSearchForm()}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        {renderTaskButton(task)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Activity Logs */}
      <div className="mt-8 bg-bw-surface rounded-lg border shadow-sm">
        <div className="px-6 py-4 border-b border-bw-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Terminal className="h-5 w-5 text-bw-muted" />
              <h2 className="text-lg font-semibold text-bw-ink">Activity Logs</h2>
            </div>
            <span className="text-sm text-bw-muted">{logs.length} entries</span>
          </div>
        </div>
        <div className="p-6">
          <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm max-h-96 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-bw-muted">No activity logs yet. Run a maintenance task to see logs here.</p>
            ) : (
              logs.map((log, index) => (
                <div key={index} className="text-green-400 mb-1">
                  {log}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* System Health Status */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-bw-surface rounded-lg border shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Activity className="h-5 w-5 text-bw-primary" />
            <h3 className="text-lg font-semibold text-bw-ink">API Status</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-bw-muted">SAM.gov API</span>
              <span className="px-2 py-1 bg-success-bg text-success text-xs font-medium rounded">
                Connected
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-bw-muted">Database</span>
              <span className="px-2 py-1 bg-success-bg text-success text-xs font-medium rounded">
                Healthy
              </span>
            </div>
          </div>
        </div>

        <div className="bg-bw-surface rounded-lg border shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Gauge className="h-5 w-5 text-bw-primary" />
            <h3 className="text-lg font-semibold text-bw-ink">Performance</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-bw-muted">Response Time</span>
              <span className="text-sm font-medium text-bw-ink">~2.3s</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-bw-muted">Success Rate</span>
              <span className="text-sm font-medium text-bw-ink">98.5%</span>
            </div>
          </div>
        </div>

        <div className="bg-bw-surface rounded-lg border shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Bell className="h-5 w-5 text-fit-moderate" />
            <h3 className="text-lg font-semibold text-bw-ink">Alerts</h3>
          </div>
          <div className="space-y-2">
            <div className="text-sm text-bw-muted">
              No active alerts
            </div>
            <div className="text-xs text-bw-muted">
              Last check: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-bw-primary-tint rounded-lg border border-blue-200 p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => runEnhancedSamTest()}
            className="flex items-center justify-center space-x-2 p-3 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors"
          >
            <Zap className="h-4 w-4" />
            <span>Quick Test</span>
          </button>
          <button
            onClick={() => loadQualityStats()}
            className="flex items-center justify-center space-x-2 p-3 bg-success text-white rounded-lg hover:bg-success transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh Stats</span>
          </button>
          <button
            onClick={() => setLogs([])}
            className="flex items-center justify-center space-x-2 p-3 bg-neutral-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            <span>Clear Logs</span>
          </button>
          <button
            onClick={() => window.open('/admin', '_blank')}
            className="flex items-center justify-center space-x-2 p-3 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors"
          >
            <Settings className="h-4 w-4" />
            <span>Admin Panel</span>
          </button>
        </div>
      </div>
    </div>
  );
}